import React, { useEffect } from "react";
import { keyframes } from "@emotion/react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import LogoButton from "core/components/LogoButton";
import ShareButton from "core/components/ShareButton";
import { useArena } from "core/components/ArenaProvider";
import { isTelegramMiniApp } from "core/utils/telegram";
import { truncate } from "core/utils/string";

/**
 * Состояние эмбеда до запуска видео, по макету «ВидеоСпортс — UI»
 * (Figma, страница EmbedVideo).
 *
 * Обложка остаётся видна целиком: затемнены только полоса с названием
 * сверху и полоса с кнопками снизу. Кнопка Play видна всегда.
 *
 * Макет нарисован для плеера 744×419. Размеры заданы через clamp() от
 * ширины и высоты iframe, чтобы в маленьком эмбеде на телефоне всё
 * ужималось пропорционально, а не наезжало друг на друга.
 */
const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/** Цвета кнопки Play из макета: обычное состояние и наведение. */
const PLAY_COLOR = "#00A876";
const PLAY_COLOR_HOVER = "#008F64";

/** 120px при 744×419, но не меньше 56px и не больше 120px. */
const PLAY_SIZE = "clamp(56px, min(16.1vw, 28.6vh), 120px)";

/** Отступы полос: 16px и 24px при ширине 744px. */
const GAP = "clamp(8px, 2.15vw, 16px)";
const GAP_LARGE = "clamp(12px, 3.2vw, 24px)";

const Root = styled("div")({
  position: "absolute",
  inset: 0,
  zIndex: 1000,
  cursor: "pointer",
  [`&:hover .EmbedPrePlay-button`]: {
    backgroundColor: PLAY_COLOR_HOVER,
  },
  // Нажали раньше, чем запустился JS плеера: крутим кольцо вокруг кнопки,
  // видео стартует само после гидратации (см. server/utils/embedEarlyTapScript)
  "&[data-queued] .EmbedPrePlay-play::after": {
    content: "''",
    position: "absolute",
    inset: -6,
    borderRadius: "50%",
    border: "3px solid rgba(255,255,255,0.9)",
    borderTopColor: "transparent",
    animation: `${spin} 0.8s linear infinite`,
    pointerEvents: "none",
  },
});

/**
 * Полоса с названием. Градиент снят с макета: 80% чёрного у верхнего
 * края, 60% под строкой текста и ноль у нижнего края полосы.
 */
const TitleBar = styled("div")({
  position: "absolute",
  zIndex: 1,
  top: 0,
  left: 0,
  right: 0,
  display: "flex",
  padding: `${GAP} ${GAP} ${GAP_LARGE}`,
  background:
    "linear-gradient(to bottom, rgba(0,0,0,0.79) 0%, rgba(0,0,0,0.6) 31%, rgba(0,0,0,0.36) 62%, rgba(0,0,0,0) 100%)",
  pointerEvents: "none",
});

const Title = styled(Typography)({
  flex: 1,
  minWidth: 0,
  color: "#fff",
  fontWeight: 600,
  lineHeight: 1.25,
  fontSize: "clamp(13px, 2.55vw, 19px)",
  textDecoration: "none",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  pointerEvents: "auto",
});

const PlayHolder = styled("div")({
  position: "absolute",
  zIndex: 1,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
});

const PlayButton = styled(ButtonBase)({
  display: "block",
  width: PLAY_SIZE,
  height: PLAY_SIZE,
  borderRadius: "50%",
  backgroundColor: PLAY_COLOR,
  boxShadow: "0 0 24px rgba(0,0,0,0.64)",
  transition: "background-color .15s ease",
  "& svg": {
    display: "block",
    width: "100%",
    height: "100%",
  },
});

/**
 * Картинка кнопки из настроек арены. Экспорт кнопки из макета идёт
 * вместе с полями под тень: кружок 120 в квадрате 168, поэтому картинка
 * в 1,4 раза крупнее кнопки, и кружок совпадает с нарисованным.
 */
const PLAY_IMAGE_SIZE = "clamp(78px, min(22.5vw, 40vh), 168px)";

const PlayImage = styled("img")({
  display: "block",
  width: PLAY_IMAGE_SIZE,
  height: PLAY_IMAGE_SIZE,
  objectFit: "contain",
});

/**
 * Нижняя полоса с «Поделиться» и логотипом. Градиент из макета:
 * от прозрачного сверху до 80% чёрного у нижнего края.
 */
const ButtonsBar = styled("div")({
  position: "absolute",
  zIndex: 1,
  left: 0,
  right: 0,
  bottom: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: GAP,
  padding: `${GAP_LARGE} ${GAP} ${GAP}`,
  background:
    "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.47) 47%, rgba(0,0,0,0.62) 70%, rgba(0,0,0,0.8) 100%)",
  // Иконки 24px вплотную, как в макете: свои отступы кнопок убираем
  "& .MuiIconButton-root, & > a": {
    padding: 0,
  },
  "& .MuiIconButton-root svg": {
    fontSize: 24,
  },
});

/** Треугольник Play из макета (EmbedPlay, Type=Green). */
function PlayTriangle() {
  return (
    <svg viewBox="24 24 120 120" aria-hidden="true" focusable="false">
      <path
        d="M62.8235 56.951C62.8235 55.395 64.5224 54.4349 65.8554 55.2378L111.744 82.875C113.034 83.6524 113.034 85.5241 111.744 86.3015L65.8554 113.939C64.5224 114.742 62.8235 113.782 62.8235 112.225V56.951Z"
        fill="#fff"
      />
    </svg>
  );
}

/** Клик по логотипу или «Поделиться» не должен запускать видео. */
const stop = (event) => event.stopPropagation();

export default function EmbedPrePlay({
  content,
  onClickPlay,
  hideShare,
  hideLogo,
  disableTitleLink,
}) {
  const { t } = useTranslation("core");
  const arena = useArena();
  const buttonUrl = arena?.embedPlayButton?.url;

  const showLogo = !hideLogo && !content.hideLogo;
  const showShare = !hideShare && !content.hideShare && !isTelegramMiniApp;
  const titleIsLink = !disableTitleLink && showLogo;

  // Нажатие, пойманное до гидратации встроенным в HTML скриптом:
  // запускаем видео сразу, второй раз жать не нужно
  useEffect(() => {
    window.__embedHydrated = true;

    if (window.__embedPlayQueued) {
      window.__embedPlayQueued = false;
      onClickPlay();
    }
  }, []);

  // Клик по самой кнопке не должен всплыть до корня: иначе onClickPlay
  // сработает дважды и плеер дважды попробует создать просмотр.
  const onClickButton = (event) => {
    event.stopPropagation();
    onClickPlay(event);
  };

  return (
    <Root onClick={onClickPlay} data-name="EmbedPrePlay">
      <TitleBar>
        <Title
          component={titleIsLink ? "a" : "div"}
          href={titleIsLink ? content.shareUrl : undefined}
          target={titleIsLink ? "_blank" : undefined}
          rel={titleIsLink ? "noopener" : undefined}
          onClick={titleIsLink ? stop : undefined}
          title={content.title}
        >
          {truncate(content.title, 160)}
        </Title>
      </TitleBar>

      <PlayHolder className="EmbedPrePlay-play">
        {buttonUrl ? (
          <ButtonBase
            aria-label={t("embedPrePlay.play")}
            onClick={onClickButton}
          >
            <PlayImage src={buttonUrl} alt="" />
          </ButtonBase>
        ) : (
          <PlayButton
            className="EmbedPrePlay-button"
            aria-label={t("embedPrePlay.play")}
            onClick={onClickButton}
          >
            <PlayTriangle />
          </PlayButton>
        )}
      </PlayHolder>

      {(showShare || showLogo) && (
        <ButtonsBar onClick={stop} data-embed-corner>
          {showShare && (
            <ShareButton
              contentId={content.id}
              shareUrl={content.shareUrl}
              shareText={content.title}
              shareDescription={content.rightholder?.name || ""}
              shareImage={content.preview}
            />
          )}
          {showLogo && (
            <LogoButton url={content.shareUrl} arena={arena} variant="full" />
          )}
        </ButtonsBar>
      )}
    </Root>
  );
}

EmbedPrePlay.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    shareUrl: PropTypes.string.isRequired,
    preview: PropTypes.string,
    hideLogo: PropTypes.bool,
    hideShare: PropTypes.bool,
    rightholder: PropTypes.shape({ name: PropTypes.string }),
  }).isRequired,
  onClickPlay: PropTypes.func.isRequired,
  hideShare: PropTypes.bool,
  hideLogo: PropTypes.bool,
  disableTitleLink: PropTypes.bool,
};
